import { ButtonGroup, Flex, Heading, MarkDown, Spinner, Text, TextContainer } from "@wayflyer/flyui";
import { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { ConfirmModal } from "shared-ui";
import { styled } from "styled-components";

export type VerifyIdentityStatus = "error" | "loading" | "ok";

export type PersonIdvState = {
  status: "requires_input" | "processing" | "verified" | "canceled" | "redacted" | "fraud";
  last_error?: {
    code: string;
    reason: string;
  };
};

export type VerifyWithConfirmation = {
  title: string;
  text: string;
};

export type VerifyIdentityText = {
  requiresInput: {
    title: string;
    text: string;
  };
};

type StartIdvFlowHandler = () => void;

export interface VerifyIdentityProps {
  personIdvState?: PersonIdvState;
  onStartIdvFlow: StartIdvFlowHandler;
  verifyIdentityStatus?: VerifyIdentityStatus;
  verifyIdentityText?: VerifyIdentityText;
  verifyWithConfirmation?: VerifyWithConfirmation;
}

interface FlowStepProps {
  onStartIdvFlow?: StartIdvFlowHandler;
  title: string;
  text: string;
  loading?: boolean;
  verifyWithConfirmation?: VerifyWithConfirmation;
}

const Container = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  max-width: 420px;
  @media (max-width: 768px) {
    margin-top: 30px;
  }
`;

const FlowStep = ({ loading = false, onStartIdvFlow, title, text, verifyWithConfirmation }: FlowStepProps) => {
  const intl = useIntl();

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const handleConfirm = useCallback(() => {
    setIsConfirmModalOpen(false);
    onStartIdvFlow && onStartIdvFlow();
  }, [onStartIdvFlow, setIsConfirmModalOpen]);

  const handleCancelConfirm = useCallback(() => setIsConfirmModalOpen(false), [setIsConfirmModalOpen]);

  const handleVerification = useCallback(() => {
    if (verifyWithConfirmation) {
      setIsConfirmModalOpen(true);
    } else {
      onStartIdvFlow && onStartIdvFlow();
    }
  }, [setIsConfirmModalOpen, verifyWithConfirmation, onStartIdvFlow]);

  return (
    <Container>
      <Flex direction="column" gap="6">
        <TextContainer spacing="4">
          <Heading>{title}</Heading>
          <Text>{text}</Text>
        </TextContainer>
        {onStartIdvFlow ? (
          loading ? (
            <Spinner size={32} />
          ) : (
            <ButtonGroup
              primaryAction={{
                content: intl.formatMessage({
                  id: "Begin verification",
                  defaultMessage: "Begin verification",
                }),
                onAction: handleVerification,
              }}
            />
          )
        ) : null}
      </Flex>

      {verifyWithConfirmation && (
        <ConfirmModal
          title={verifyWithConfirmation.title}
          primaryAction={{
            content: intl.formatMessage({
              id: "I confirm",
              defaultMessage: "I confirm",
            }),
            onAction: handleConfirm,
          }}
          isOpen={isConfirmModalOpen}
          secondaryAction={{ onAction: handleCancelConfirm }}
        >
          <MarkDown markdown={verifyWithConfirmation.text} />
        </ConfirmModal>
      )}
    </Container>
  );
};

/**
 * VerifyIdentity component
 */
export const VerifyIdentity = ({
  verifyIdentityStatus = "ok",
  personIdvState,
  onStartIdvFlow,
  verifyIdentityText,
  verifyWithConfirmation,
}: VerifyIdentityProps) => {
  const intl = useIntl();
  if (verifyIdentityStatus === "error") {
    return (
      <FlowStep
        title={intl.formatMessage({ id: "Try again later", defaultMessage: "Try again later" })}
        text={intl.formatMessage({
          id: "Please try identity verification in a few moments.",
          defaultMessage: "Please try identity verification in a few moments.",
        })}
        onStartIdvFlow={onStartIdvFlow}
        verifyWithConfirmation={verifyWithConfirmation}
      />
    );
  }
  switch (personIdvState?.status) {
    case undefined:
      return null;
    case "requires_input":
      return (
        <FlowStep
          loading={verifyIdentityStatus === "loading"}
          title={
            verifyIdentityText?.requiresInput.title ||
            intl.formatMessage({
              id: "Start identity verification",
              defaultMessage: "Start identity verification",
            })
          }
          text={
            verifyIdentityText?.requiresInput.text ||
            intl.formatMessage({
              id: `Start identity verification description`,
              defaultMessage: `Before starting you will need a valid government-issued photo ID document.
              If you are completing the identify verification on a computer or laptop you will also require a webcam.`,
            })
          }
          onStartIdvFlow={onStartIdvFlow}
          verifyWithConfirmation={verifyWithConfirmation}
        />
      );
    case "canceled":
      return (
        <FlowStep
          title={intl.formatMessage({
            id: "This link is out of date",
            defaultMessage: "This link is out of date",
          })}
          text={intl.formatMessage({
            id: "The link no longer required full",
            defaultMessage:
              "The link you have used to perform identity verification is no longer valid. If you have received a new link please use the most recent one emailed to you. If you have not received a new link and still think identity verification is required of you please contact support.",
          })}
        />
      );
    case "processing":
      return (
        <FlowStep
          title={intl.formatMessage({
            id: "Identity document submitted",
            defaultMessage: "Identity document submitted",
          })}
          text={intl.formatMessage({
            id: "Thanks for submitting your identity document. We’re processing it. You can close this tab.",
            defaultMessage:
              "Thanks for submitting your identity document. We’re processing it. You can close this tab.",
          })}
        />
      );
    case "verified":
      return (
        <FlowStep
          title={intl.formatMessage({ id: "You're all set", defaultMessage: "You're all set" })}
          text={intl.formatMessage({
            id: "You're all set message",
            defaultMessage:
              "Thank you for verifying your identity. We successfully processed your document. You can close this tab.",
          })}
        />
      );
    case "fraud":
      return (
        <FlowStep
          title={intl.formatMessage({
            id: "Identity verification failed",
            defaultMessage: "Identity verification failed",
          })}
          text={intl.formatMessage({
            id: "Identify verification failed message",
            defaultMessage: "Identify verification failed. We will contact you to try again if necessary.",
          })}
        />
      );
    default:
      return null;
  }
};
